import {
  getObjectEntries,
  isEmpty,
  type NonEmptyArray,
} from '@festina-life/core'

import type {
  RankedItem,
  RenderedCategory,
  SearchableCategory,
  SearchableItemGroup,
  SearchableOption,
} from './types'
import type { CommandPaletteLocalStrorageType } from './useCommandPaletteLocalStorage'

export const toMergedApiResponse = (
  apiResponses: NonNullable<SearchableOption['apiResponse']>[],
): NonNullable<SearchableOption['apiResponse']> => ({
  data: apiResponses.some(checkApiDataResponse),
  isLoading: apiResponses.some(({ isLoading }) => isLoading),
  error: apiResponses.find(({ error }) => !!error)?.error ?? null,
})

export const checkApiDataResponse = (
  apiResponses: SearchableOption['apiResponse'],
) => {
  const data = apiResponses?.data

  // If no apiResponses are provided, we assume there is data to show
  if (!apiResponses) {
    return true
  } else if (isEmpty(data)) {
    return false
  } else if (typeof data === 'boolean') {
    return data
  } else if (Array.isArray(data)) {
    return data.length > 0
  } else if (typeof data === 'object') {
    // Checks that there are data entries
    // We exclude _type
    // And check that the other seperate key has more than just an _type also

    const obj = data as Record<string, unknown>
    const entriesWithoutType = Object.entries(obj).filter(
      ([key]) => key !== '_type',
    )

    if (entriesWithoutType.length === 0) {
      return false
    }

    // Check if any non-_type key has real data
    return entriesWithoutType.some(([, value]) => {
      if (isEmpty(value)) {
        return false
      }

      // If it's an object, check it's not just {_type: ...}
      if (typeof value === 'object' && !Array.isArray(value)) {
        const valueKeys = Object.keys(value).filter((k) => k !== '_type')
        return valueKeys.length > 0
      }

      // For arrays, check they're not empty
      if (Array.isArray(value)) {
        return value.length > 0
      }

      return true
    })
  } else {
    return true
  }
}

export const processCategoryGroup = ({
  categories,
  group,
  options,
  shouldSortOnTitle,
  favouriteKeys,
  shouldHideFavourites,
  shouldHideUnauthorized,
  shouldHideWarnings,
}: {
  group: SearchableItemGroup
  categories: Record<string & 'favourites', RenderedCategory>
  options: RankedItem[]
  shouldSortOnTitle: boolean
} & CommandPaletteLocalStrorageType): {
  items: RankedItem[]
  category: RenderedCategory
}[] => {
  const result: { items: RankedItem[]; category: RenderedCategory }[] = []

  // Process options to add 'favourites' category to items with matching favouriteKey
  const processedOptions = shouldHideFavourites
    ? options
    : options.map((option) =>
        option.favouriteKey && favouriteKeys.includes(option.favouriteKey)
          ? {
              ...option,
              categories: [
                ...option.categories,
                'favourites',
              ] satisfies NonEmptyArray<string>,
            }
          : option,
      )

  getObjectEntries(categories).forEach(([category, renderedCategory]) => {
    let items = processedOptions.filter((option) => {
      const showInsufficientAccessRights = shouldHideUnauthorized
        ? option?.hasSufficientAccessRights !== false
        : true

      const hasWarnings = option.nestedView
        ? !!option.nestedView?.nestedChildren?.some((x) => x.warnings?.length)
        : !!option.warnings?.length
      const isVisibleDespiteWarnings = shouldHideWarnings ? !hasWarnings : true

      return (
        option.group === group &&
        option.categories.includes(category as SearchableCategory) &&
        option.hasRequiredComponents &&
        showInsufficientAccessRights &&
        isVisibleDespiteWarnings
      )
    })

    if (shouldSortOnTitle) {
      // Handle custom if the b.id starts with workflow.
      items = items.sort((a, b) => {
        const isAWorkflow = a.isWorkflow
        const isBWorkflow = b.isWorkflow

        if (isAWorkflow && !isBWorkflow) {
          return -1
        }
        if (!isAWorkflow && isBWorkflow) {
          return 1
        }

        return a.title.localeCompare(b.title)
      })
    }

    if (items.length > 0) {
      result.push({
        category: renderedCategory,
        // Needs a "clone" here, as the object identify needs to be unique as one item can be in multiple categories.
        items: items.map((item) => ({ ...item })) as RankedItem[],
      })
    }
  })

  return result
}
